import {
  Either, Fail, Maybe, Success,
} from 'monet';
import LeftResponse from '@/storage/LeftResponse';
import { getISODateStringByString } from '@/mixins/dateMixin';
import { makeStatusBySchema } from '@/storage/gateways/EgiszIemkGateway';
import { Diagnosis } from './VisitGateway';

const EXCEL_TYPE_REQUEST = 'application/vnd.ms-excel';
const JSON_TYPE_REQUEST = 'application/json';

/**
 * @description Адрес.
 * @param {number|null} addressTypeId Идентификатор типа адреса.
 * @param {string}      apartment Номер квартиры.
 * @param {string}      building Номер дома.
 * @param {string}      city Город.
 * @param {string}      district Район.
 * @param {string}      postalCode Почтовый индекс.
 * @param {string}      street Улица.
 * @constructor
 */
export function Address(
  addressTypeId = null,
  apartment = '',
  building = '',
  city = '',
  district = '',
  postalCode = '',
  street = '',
) {
  this.addressTypeId = addressTypeId;
  this.apartment = apartment;
  this.building = building;
  this.city = city;
  this.district = district;
  this.postalCode = postalCode;
  this.street = street;

  let start = JSON.stringify(this);
  this.refresh = () => {
    start = JSON.stringify(this);
  };
  this.isChanged = () => JSON.stringify(this) !== start;
}

/**
 * @description Анамнез.
 * @param {string}      allergicReactions        Аллергические реакции.
 * @param {string}      bite                     Прикус.
 * @param {string}      complaints               Жалобы.
 * @param {string}      externalExamination      Данные объективного исследования, внешний осмотр.
 * @param {string}      labData                  Данные рентгеновских, лабораторных исследований.
 * @param {string}      oralMucosa               Состояние слизистой оболочки полости рта.
 * @param {string}      pastDiseases             Перенесённые и сопутствующие заболевания.
 * @param {string}      presentDisease           Развитие настоящего заболевания.
 * @param {string}      radiationDoseSummary     Лучевое облучение.
 * @param {string}      medicationsTaken         Принимаемые лекарственные препараты.
 * @param {string}      actualDiseaseProgression Развитие настоящего заболевания.
 * @param {string}      examinationOralCavity    Осмотр преддверия и полости рта.
 * @param {string}      hygieneIndex             Индекс гигиены.
 * @param {string}      cpuIndex                 Индекс КПУ.
 * @param {boolean}     isMilkBite               Молочный прикус.
 * @constructor
 */
export function Anamnesis(
  allergicReactions = '',
  bite = '',
  complaints = '',
  externalExamination = '',
  labData = '',
  oralMucosa = '',
  pastDiseases = '',
  presentDisease = '',
  radiationDoseSummary = '',
  medicationsTaken = '',
  actualDiseaseProgression = '',
  examinationOralCavity = '',
  hygieneIndex = '',
  cpuIndex = '',
  isMilkBite = false,
) {
  this.allergicReactions = allergicReactions;
  this.bite = bite;
  this.complaints = complaints;
  this.externalExamination = externalExamination;
  this.labData = labData;
  this.oralMucosa = oralMucosa;
  this.pastDiseases = pastDiseases;
  this.presentDisease = presentDisease;
  this.radiationDoseSummary = radiationDoseSummary;
  this.isMilkBite = isMilkBite;
  this.medicationsTaken = medicationsTaken;
  this.actualDiseaseProgression = actualDiseaseProgression;
  this.examinationOralCavity = examinationOralCavity;
  this.hygieneIndex = hygieneIndex;
  this.cpuIndex = cpuIndex;

  let start = JSON.stringify(this);
  this.refresh = () => {
    start = JSON.stringify(this);
  };
  this.isChanged = () => JSON.stringify(this) !== start;

  this.appendPhraseIntoField = (property, phrase) => {
    this[property] = `${this[property]} ${phrase}`;
    return this;
  };
}
/**
 * @description Контакты.
 * @param {string}  email             Адрес электронной почты.
 * @param {boolean} isAllowedMailing  Разрешены рассылки.
 * @param {boolean} isNoCalls         Запрещены звонки.
 * @param {Object}  phoneList         Список телефонных номеров.
 * @constructor
 */
function Contacts(
  email,
  isAllowedMailing,
  isNoCalls,
  phoneList,
) {
  this.getEmail = () => email;
  this.getPhoneList = () => phoneList;
  this.isAllowedMailing = () => isAllowedMailing;
  this.isNoCalls = () => isNoCalls;
}

/**
 * @description Полость рта пациента.
 * @param {Tooth[]} teeth Зубы.
 * @constructor
 */
export function DentalCavities(teeth = []) {
  /**
   * @type {Tooth[]}
   */
  this.teeth = teeth;
  let start = JSON.stringify(this);
  this.refresh = () => {
    start = JSON.stringify(this);
  };
  this.isChanged = () => JSON.stringify(this) !== start;
}
/**
 * @description Документы.
 * @param {string} iin
 * @param {string} snils
 * @constructor
 */
export function Documents(
  iin = '',
  snils = '',
) {
  this.iin = iin;
  this.snils = snils;

  let start = JSON.stringify(this);
  this.refresh = () => {
    start = JSON.stringify(this);
  };
  this.isChanged = () => {
    const startEntity = JSON.parse(start);
    return this.iin !== startEntity.iin
      || this.snils !== startEntity.snils;
  };
}

/**
 * @description Личные данные
 * @param {boolean} hasEgiszDataRefusal Имеет отказ в предоставлении данных для ЕГИСЗ.
 * @constructor
 */
export function IndividualData(
  hasEgiszDataRefusal = false,
) {
  let initialHasEgiszDataRefusal = hasEgiszDataRefusal;
  this.hasEgiszDataRefusal = hasEgiszDataRefusal;
  let start = JSON.stringify(this);
  this.refresh = () => {
    initialHasEgiszDataRefusal = this.isEgiszDataRefusalChanged;
    start = JSON.stringify(this);
  };
  this.isChanged = () => JSON.stringify(this) !== start;
  this.isEgiszDataRefusalChanged = () => initialHasEgiszDataRefusal !== this.hasEgiszDataRefusal;
}

/**
 * @description Запрос на редактирование личных данных пациента
 * @param {IndividualData} individualData
 * @param {numeric}   patientId
 * @constructor
 */
export function EditPatientIndividualDataRequest(individualData, patientId) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    has_egisz_data_refusal: individualData.hasEgiszDataRefusal,
  });
}

/**
 * @description Отправить запрос на редактирование адреса.
 * @param {Address} address Адрес.
 * @param {number}  patientId Идентификатор пациента.
 * @constructor
 */
export function EditAddressRequest(address, patientId) {
  this.toQuery = () => ({
    address_type_id: address.addressTypeId,
    apartment: address.apartment,
    building: address.building,
    city: address.city,
    district: address.district,
    postal_code: address.postalCode,
    street: address.street,
  });
  this.getPatientId = () => patientId;
}

/**
 * @description Удостоверение личности.
 * @param {string} address
 * @param {string} number
 * @param {string} serial
 * @param {string} type
 * @param {string} whenIssued
 * @param {string} whoIssued
 * @param {string} departmentCode
 * @param {string} inn
 * @param {string} livingAddress
 * @param {string} snils
 * @constructor
 */
export function Identification(
  address = '',
  number = '',
  serial = '',
  type = '',
  whenIssued = '',
  whoIssued = '',
  departmentCode = '',
  inn = '',
  livingAddress = '',
  snils = '',
) {
  this.address = address;
  this.number = number;
  this.serial = serial;
  this.type = type;
  this.whenIssued = whenIssued;
  this.whoIssued = whoIssued;
  this.departmentCode = departmentCode;
  this.inn = inn;
  this.snils = snils;
  this.livingAddress = livingAddress;
  let start = JSON.stringify(this);
  this.refresh = () => { start = JSON.stringify(this); };
  this.isChanged = () => {
    const startEntity = JSON.parse(start);
    return this.address !== startEntity.address
      || this.number !== startEntity.number
      || (
        this.serial.replace(/[\s-]/g, '') !== startEntity.serial.replace(/[\s-]/g, '')
        && this.type !== 'foreign-passport'
      )
      || this.type !== startEntity.type
      || this.whenIssued !== startEntity.whenIssued
      || this.whoIssued !== startEntity.whoIssued
      || this.departmentCode !== startEntity.departmentCode
      || this.inn !== startEntity.inn
      || this.snils !== startEntity.snils
      || this.livingAddress !== startEntity.livingAddress;
  };
}

/**
 * Номер телефона
 *
 * @param {string} phone
 * @param {string} phoneType
 * @param {string} isMain
 * @param {string} note
 * @constructor
 */
export function Phone(
  phone = '',
  phoneType = 'mobile',
  isMain = false,
  note = '',
) {
  this.number = phone;
  this.type = phoneType;
  this.isMain = isMain;
  this.note = note;
}

/**
 * @description Понятие "представитель пациента"
 * @param {string}         birthDate
 * @param {string}         document
 * @param {Identification} identification
 * @param {string}         name
 * @param {string}         patronymic
 * @param {string}         relation
 * @param {string}         gender
 * @param {string}         surname
 * @param                  id
 * @param {boolean}        isNew
 * @param {Phone}          phone
 * @param {boolean}        hasWhatsApp
 * @param {string}         iin
 * @constructor
 */
export function Guardian(
  birthDate = '',
  document = '',
  identification = new Identification(),
  name = '',
  patronymic = '',
  relation = '',
  gender = '',
  surname = '',
  id = undefined,
  isNew = true,
  phone = new Phone(),
  hasWhatsApp = false,
  iin = '',
) {
  this.birthDate = birthDate;
  this.document = document;
  this.identification = identification;
  this.name = name;
  this.patronymic = patronymic;
  this.relation = relation;
  this.gender = gender;
  this.surname = surname;
  this.id = id;
  this.isNew = isNew;
  this.phone = phone;
  this.fullName = `${this.surname} ${this.name} ${this.patronymic}`;
  this.fullGuardianRelation = relation;
  this.hasWhatsApp = hasWhatsApp;
  this.iin = iin;
  let start = JSON.stringify(this);
  this.refresh = () => {
    this.fullName = `${this.surname} ${this.name} ${this.patronymic}`;
    start = JSON.stringify(this);
  };
  const ctx = this;
  this.isChanged = () => JSON.stringify(this) !== start;
  function ToRequest(patientId) {
    this.getPatientId = () => patientId;
    this.toQuery = () => ({
      birth_date: ctx.birthDate,
      document: ctx.document,
      identification: {
        address: ctx.identification.address,
        number: ctx.identification.number,
        serial: ctx.identification.serial,
        type: ctx.identification.type,
        whenIssued: getISODateStringByString(ctx.identification.whenIssued),
        whoIssued: ctx.identification.whoIssued,
        departmentCode: ctx.identification.departmentCode,
        living_address: ctx.identification.livingAddress,
        snils: ctx.identification.snils,
      },
      name: ctx.name,
      patronymic: ctx.patronymic,
      relation: ctx.relation,
      gender: ctx.gender,
      surname: ctx.surname,
      phone: ctx.phone.number,
      phone_type: ctx.phone.type,
      phone_is_main: ctx.phone.isMain,
      note: ctx.phone.note,
      id: ctx.id,
      iin: ctx.iin,
    });
  }
  this.createRequest = (patientId) => new ToRequest(patientId);
}

/**
 * @description Запрос на редактирование анамнеза пациента.
 * @param {Anamnesis} anamnesis
 * @param {numeric}   patientId
 */
export function EditPatientAnamnesisRequest(anamnesis, patientId) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    allergic_reactions: anamnesis.allergicReactions,
    bite: anamnesis.bite,
    complaints: anamnesis.complaints,
    external_examination: anamnesis.externalExamination,
    lab_data: anamnesis.labData,
    oral_mucosa: anamnesis.oralMucosa,
    past_diseases: anamnesis.pastDiseases,
    present_disease: anamnesis.presentDisease,
    radiation_dose_summary: anamnesis.radiationDoseSummary,
    is_milk_bite: anamnesis.isMilkBite,
    medications_taken: anamnesis.medicationsTaken,
    actual_disease_progression: anamnesis.actualDiseaseProgression,
    examination_oral_cavity: anamnesis.examinationOralCavity,
    hygiene_index: anamnesis.hygieneIndex,
    cpu_index: anamnesis.cpuIndex,
  });
}
/**
 * @description Запрос на редактирование контактов пациента.
 * @param {PatientContacts} contacts
 * @param {numeric}         patientId
 */
export function EditPatientContactsRequest(contacts, patientId) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    email: contacts.email,
    is_allowed_mailing: contacts.isAllowedMailing,
    no_call: contacts.isNoCalls,
    phone_list: contacts.phoneList.map(({
      type, value, isMain, note,
    }) => ('' || {
      type,
      value,
      is_main: isMain,
      note,
    })),
  });
}
/**
 * @description Запрос на редактирование полости рта пациента.
 * @param {DentalCavities} dentalCavities
 * @param {numeric}        patientId
 */
export function EditPatientDentalCavitiesRequest(dentalCavities, patientId) {
  const teeth = dentalCavities.teeth.map((tooth) => ({
    number: tooth.number,
    diagnoses: {
      upper: tooth.upper,
      lower: tooth.lower,
    },
  }));
  this.getPatientId = () => patientId;
  this.toQuery = () => ({ teeth });
}
/**
 * @description Запрос на редактирование документов пациента.
 * @param {Documents} documents
 * @param {numeric}   patientId
 */
export function EditPatientDocumentsRequest(documents, patientId) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    iin: documents.iin,
    snils: documents.snils,
  });
}
/**
 * @description Запрос на редактирование удостоверения личности пациента.
 * @param {Identification} identification
 * @param {numeric}        patientId
 */
export function EditPatientIdentificationRequest(identification, patientId) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    address: identification.address,
    issued_date: getISODateStringByString(identification.whenIssued),
    issuer_name: identification.whoIssued,
    number: identification.number,
    series: identification.serial,
    type: identification.type,
    department_code: identification.departmentCode,
    inn: identification.inn,
    living_address: identification.livingAddress,
  });
}
/**
 * @description Запрос на редактирование удостоверения личности иностранного паспорта пациента.
 * @param {Identification} identification
 * @param {numeric}        patientId
 */
export function EditPatientIdentificationForeignPasswordRequest(
  identification,
  patientId,
) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    issued_date: getISODateStringByString(identification.whenIssued),
    issuer_name: identification.whoIssued,
    number: identification.number,
    series: '',
    inn: identification.inn,
    type: identification.type,
    living_address: identification.livingAddress,
    address: identification.address,
  });
}
/**
 * @description Запрос на редактирование медкарты пациента.
 * @param {Medcard} medcard
 * @param {numeric} patientId
 */
export function EditPatientMedcardRequest(medcard, patientId) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    birth_date: medcard.birthDate,
    labels: medcard.labels.map((item) => item.id),
    medical_card_created_at: medcard.medicalCardCreatedAt,
    medical_card_number: medcard.medicalCardNumber,
    name: medcard.name,
    note: medcard.note,
    patronymic: medcard.patronymic,
    sex_id: medcard.sexId || '',
    surname: medcard.surname,
    workplace: medcard.workplace,
  });
}

/**
 * @description Запрос на редактирование персональных данных пациента.
 * @param {string} address
 * @param {string} medicalCardNumber
 * @param {string} passportNumber
 * @param {string} passportSerial
 * @param {string} passportWhoIssued
 * @param {string} phone
 * @param {string} patientId
 * @constructor
 */
export function EditPatientPersonalDataRequest(
  address,
  medicalCardNumber,
  passportNumber,
  passportSerial,
  passportWhoIssued,
  patientId,
  phone,
) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    address,
    medical_card_number: medicalCardNumber,
    passport_number: passportNumber,
    passport_serial: passportSerial,
    passport_who_issued: passportWhoIssued,
    phone,
  });
}

/**
 * @description Запрос на редактирование зубов.
 * @param {number}                visitId Идентификатор визита.
 * @param {Object<number,Part[]>} teeth   Зубы.
 * @constructor
 */
export function EditTeethRequest(visitId, teeth) {
  this.getVisitId = () => visitId;
  this.toQuery = () => {
    const data = Object.keys(teeth).map((number) => ({
      number,
      parts: teeth[number].map(
        (part) => ({
          id: part.id,
          diagnoses: part.diagnoses,
        }),
      ),
    }));
    return { teeth: data };
  };
}

/**
 * @description Запрос на получение списка совпадающих пациентов.
 * @param {string}   name       Имя.
 * @param {string}   patronymic Отчество.
 * @param {string[]} phones     Номера телефона.
 * @param {string}   surname    Фамилия.
 * @constructor
 */
export function GetMatchedPatientsRequest(name, patronymic, phones, surname) {
  this.toQuery = () => ({
    name,
    patronymic,
    phones,
    surname,
  });
}

/**
 * @description Медкарта.
 * @param {string}  birthDate
 * @param {string}  birthDateTitle
 * @param {string}  medicalCardCreatedAt
 * @param {string}  medicalCardNumber
 * @param {string}  name
 * @param {string}  note
 * @param {string}  patronymic
 * @param {?number} sexId
 * @param {string}  surname
 * @param {Array}   labels
 * @param {string}  workplace
 * @constructor
 */
export function Medcard(
  birthDate = '',
  birthDateTitle = '',
  medicalCardCreatedAt = '',
  medicalCardNumber = '',
  name = '',
  note = '',
  patronymic = '',
  sexId = null,
  surname = '',
  labels = [],
  workplace = '',
) {
  this.birthDateTitle = birthDateTitle;
  this.birthDate = birthDate;
  this.medicalCardCreatedAt = medicalCardCreatedAt;
  this.medicalCardNumber = medicalCardNumber;
  this.name = name;
  this.note = note;
  this.patronymic = patronymic;
  this.sexId = sexId;
  this.surname = surname;
  this.labels = labels;
  this.workplace = workplace;

  let start = JSON.stringify(this);
  this.refresh = () => {
    start = JSON.stringify(this);
  };
  this.isChanged = () => JSON.stringify(this) !== start;
}
/**
 * @description Пациент.
 * @param {string}              advance
 * @param {string}              amountPayments
 * @param {string}              cdaCompleteness
 * @param {string}              contractNumber
 * @param {string}              debt
 * @param {number}              id
 * @param {IemkReportingStatus} iemkStatus
 * @param {string}              firstName
 * @param {string}              lastName
 * @param {string}              patronymic
 * @param {string}              medicalCardNumber
 * @param {string}              lastVisit
 * @param {any}                 lastVisitDate
 * @param {any}                 nearAppointment
 * @param {string}              nearAppointmentDate
 * @param {string}              phone
 * @param {string}              fullName
 * @param {string}              surnameWithInitials
 * @param {string}              age
 * @param {number}              index
 * @param {number}              hasVisit
 * @param {boolean}             isArchived
 * @constructor
 */
function Patient(
  advance,
  amountPayments,
  cdaCompleteness,
  contractNumber,
  debt,
  id,
  iemkStatus,
  firstName,
  lastName,
  patronymic,
  medicalCardNumber,
  lastVisit,
  lastVisitDate,
  nearAppointment,
  nearAppointmentDate,
  phone,
  fullName,
  surnameWithInitials,
  age,
  index,
  hasVisit,
  isArchived,
) {
  this.advance = advance;
  this.amountPayments = amountPayments;
  this.cdaCompleteness = cdaCompleteness;
  this.contractNumber = contractNumber;
  this.debt = debt;
  this.id = id;
  this.iemkStatus = iemkStatus;
  this.firstName = firstName;
  this.lastName = lastName;
  this.patronymic = patronymic;
  this.medicalCardNumber = medicalCardNumber;
  this.lastVisit = lastVisit;
  this.lastVisitDate = lastVisitDate;
  this.nearAppointment = nearAppointment;
  this.nearAppointmentDate = nearAppointmentDate;
  this.phone = phone;
  this.fullName = fullName;
  this.surnameWithInitials = surnameWithInitials;
  this.age = age;
  this.index = index;
  this.hasVisit = hasVisit;
  this.isArchived = isArchived;
}

/**
 * @description Пациент с удостоверение личности.
 *
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} patronymic
 * @param {string} birthDate
 * @param {string} sexId
 * @param {string} snils
 * @param {object} identification
 * @param {string} phone
 * @param {string} phoneType
 * @constructor
 */
function PatientWithIdentification(
  firstName,
  lastName,
  patronymic,
  birthDate,
  sexId,
  snils,
  identification,
  phone,
  phoneType,
) {
  this.birthDate = birthDate;
  this.sexId = sexId;
  this.snils = snils;
  this.identification = identification;
  this.firstName = firstName;
  this.lastName = lastName;
  this.patronymic = patronymic;
  this.phone = phone;
  this.phoneType = phoneType;
}
/**
 * @description Зуб.
 * @param {number} number
 * @param {string} upper
 * @param {string} lower
 */
export function Tooth(number, upper, lower) {
  this.number = number;
  this.upper = upper;
  this.lower = lower;
}

export function PersonalData(
  address = '',
  medicalCardNumber = '',
  passportNumber = '',
  passportSerial = '',
  passportWhoIssued = '',
  phone = '',
) {
  this.address = address;
  this.medicalCardNumber = medicalCardNumber;
  this.passportNumber = passportNumber;
  this.passportSerial = passportSerial;
  this.passportWhoIssued = passportWhoIssued;
  this.phone = phone;
}

/**
 * @description Шлюз пациентов.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function PatientGateway(axios) {
  /**
   * @description Добавить в архив.
   * @param {number} patientId
   * @param {number} reasonId
   * @param {boolean} restrictAppointment
   * @param {boolean} needCheckFutureAppointments
   * @return {Promise}
   */
  this.addToArchive = (
    patientId,
    reasonId,
    restrictAppointment,
    needCheckFutureAppointments,
  ) => axios
    .post(
      `/api/patients/${patientId}/archive`,
      {
        reason_id: reasonId,
        restrict_appointment: restrictAppointment,
        check_future_appointments: needCheckFutureAppointments,
      },
    )
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const data = await error.response.data;
          const { causes, title } = data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Проверить заполненность удостоверения личности пациента.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise}
   */
  this.checkIdentificationCompleteness = (patientId) => axios
    .get(`/api/patients/${patientId}/identification-completeness`)
    .then(
      async (response) => Success(response.data.data || []),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error;
      },
    );
  /**
   * @description Выгрузить информацию о пациенте.
   * @param {number} patientId
   * @returns {Promise<Either>}
   */
  this.download = (patientId) => axios
    .get(`/api/patients/${patientId}/download`, { responseType: 'blob' })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const data = await error.response.data.text();
          return Either.left(JSON.parse(data).title);
        }
        throw error;
      },
    );
  /**
   * @description Выгрузить информацию о пациентах.
   * @returns {Promise<Either>}
   */
  this.downloadList = () => axios
    .post('/api/patients/downloads')
    .then(
      async (response) => {
        const { data } = response;
        return Either.right({ title: data.title, downloadId: data.download_id });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );

  /**
   * @description Выгрузить информацию о пациентах.
   * @returns {Promise<Either>}
   */
  this.downloadListById = (downloadId) => axios
    .get(`/api/patients/downloads/${downloadId}`, { responseType: 'blob' })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const data = await error.response.data.text();
          const { causes, title } = JSON.parse(data);
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Установить рекламный канал пациенту.
   * @param {number} patientId            Идентификатор пациента.
   * @param {number} advertisingChannelId Идентификатор рекламного канала.
   * @returns {Promise}
   */
  this.setAdvertisingChannel = (patientId, advertisingChannelId) => axios
    .put(
      `/api/patients/${patientId}/advertising-channel`,
      { advertising_channel_id: advertisingChannelId },
    )
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Fail(error.response.data);
        }
        if (error.isAxiosError && error.response.status === 403) {
          return Fail(error.response.data);
        }
        if (error.isAxiosError && error.response.status === 404) {
          return Fail(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на создание профиля пациента.
   * @param {any} request
   * @returns {Promise}
   */
  this.sendCreatePatient = (request) => axios
    .post('/api/patient-profiles', request)
    .then(
      async (response) => {
        const result = response.data;
        return Success({
          id: result.patient_id,
          message: result.message,
        });
      },
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const failurePairs = (error.response.data.causes || [])
            .map((cause) => [cause.source, cause.details]);
          const causes = Object.fromEntries(failurePairs) || {};
          const { title } = error.response.data;
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на изменение профиля пациента.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise}
   */
  this.getAnamnesis = async (patientId) => {
    const makeAnamnesis = (anamnesis) => new Anamnesis(
      anamnesis.allergic_reactions,
      anamnesis.bite,
      anamnesis.complaints,
      anamnesis.external_examination,
      anamnesis.lab_data,
      anamnesis.oral_mucosa,
      anamnesis.past_diseases,
      anamnesis.present_disease,
      anamnesis.radiation_dose_summary,
      anamnesis.medications_taken,
      anamnesis.actual_disease_progression,
      anamnesis.examination_oral_cavity,
      anamnesis.hygiene_index,
      anamnesis.cpu_index,
      anamnesis.is_milk_bite,
    );
    const makeDentalCavities = (teeth) => new DentalCavities(
      teeth.map(
        (tooth) => new Tooth(tooth.number, tooth.diagnoses.upper, tooth.diagnoses.lower),
      ),
    );
    return axios
      .get(`/api/patients/${patientId}/anamnesis`)
      .then(
        async (response) => {
          const anamnesis = makeAnamnesis(response.data.anamnesis);
          const dentalCavities = makeDentalCavities(response.data.dental_cavities.teeth);
          const { errors } = response.data;
          return Either.right({ anamnesis, dentalCavities, errors });
        },
        async (error) => {
          if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
            return Either.left(error.response.data.title);
          }
          throw error;
        },
      );
  };
  /**
   * @description Получить список пациентов.
   * @param {{type:String, order:String}}  sort            параметры сортировки
   * @param {number}                       page            Номер страницы.
   * @param {string}                       searchQuery     Поисковой запрос.
   * @param {object}                       searchBornDate  Поиск по дате рождения.
   * @param {object}                       searchVisitDate Поиск по дате визита.
   * @param {boolean}                      showArchived    Показывать архивных.
   * @returns {Promise<Either>}
   */
  this.getAll = (
    sort = { type: '', order: '' },
    page = 1,
    searchQuery = '',
    searchBornDate = {},
    searchVisitDate = {},
    showArchived = false,
  ) => {
    const getFullDate = (isoDate) => {
      const options = {
        day: 'numeric',
        month: 'numeric',
        year: '2-digit',
      };
      const date = new Date(isoDate);
      const formatted = date.toLocaleDateString('ru-RU', options);
      return `${formatted}`;
    };
    return axios
      .get(
        '/api/patients',
        {
          headers: { accept: JSON_TYPE_REQUEST },
          params: {
            sort_type: sort.type,
            sort_order: sort.order,
            page,
            search_query: searchQuery,
            from: searchBornDate.from ?? '',
            to: searchBornDate.to ?? '',
            visit_date_from: searchVisitDate.from ?? '',
            visit_date_to: searchVisitDate.to ?? '',
            show_archived: showArchived ? 1 : 0,
          },
        },
      )
      .then(
        async (response) => {
          if (Array.isArray(response.data.data)) {
            const patients = (response.data.data || []).map(
              (patient, key) => (
                new Patient(
                  patient.advance,
                  patient.amount_payments,
                  patient.cda_completeness,
                  patient.contract_number,
                  patient.debt,
                  patient.id,
                  makeStatusBySchema(patient.iemk_status),
                  patient.name,
                  patient.surname,
                  patient.patronymic,
                  patient.medical_card_number || '-',
                  patient.last_visit,
                  patient.last_visit_date ? getFullDate(patient.last_visit_date) : '-',
                  patient.near_appointment,
                  patient.near_appointment_date || '-',
                  patient.phone,
                  patient.full_name,
                  patient.surname_with_initials,
                  patient.age,
                  key,
                  patient.has_visit,
                  patient.is_archive,
                )
              ),
            );
            return Either.right({
              actions: response.data.actions,
              columns: response.data.columns,
              patients,
              pageCount: response.data.perPage,
            });
          }
          const keys = Object.keys(response.data.data);
          const patients = [];
          keys.forEach(
            (key) => {
              const patient = response.data.data[key];
              patients.push(
                new Patient(
                  patient.advance,
                  patient.amount_payments,
                  patient.cda_completeness,
                  patient.contract_number,
                  patient.debt,
                  patient.id,
                  makeStatusBySchema(patient.iemk_status),
                  patient.name,
                  patient.surname,
                  patient.patronymic,
                  patient.medical_card_number || '-',
                  patient.last_visit,
                  patient.last_visit_date ? getFullDate(patient.last_visit_date) : '-',
                  patient.near_appointment,
                  patient.near_appointment_date || '-',
                  patient.phone,
                  patient.full_name,
                  patient.surname_with_initials,
                  patient.age,
                  parseInt(key, 10),
                  patient.has_visit,
                ),
              );
            },
          );
          return Either.right({
            actions: response.data.actions,
            columns: response.data.columns,
            patients,
            pageCount: response.data.perPage,
          });
        },
        async (error) => {
          if (error.isAxiosError && error.response.status === 403) {
            const { title } = error.response.data;
            return Either.left(title);
          }
          throw error;
        },
      );
  };
  /**
   * @description Получить список сотрудников в виде таблицы.
   * @param {{type:String, order:String}} sort параметры сортировки
   * @return {Promise<Either>}
   */
  this.getAllBySheet = (sort = { type: '', order: '' }) => axios
    .get('/api/patients', {
      headers: { accept: EXCEL_TYPE_REQUEST },
      responseType: 'blob',
      params: {
        sort_type: sort.type,
        sort_order: sort.order,
      },
    })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на получение совпадающих пациентов.
   * @param {GetMatchedPatientsRequest} request
   * @returns {Promise}
   */
  this.getMatchedPatients = (request) => axios
    .post('/api/matched-patients', request.toQuery())
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        if (error.isAxiosError) {
          if (error.response && error.response.status === 404) {
            return Fail({ title: error.response.title });
          }
          if (!error.response) {
            throw new Error('Network error: сервер по адресу /api/matched-patients не ответил на запрос');
          }
        }
        throw error;
      },
    );
  /**
   * @description Получить список причин архивирования.
   * @return {Promise<Either>}
   */
  this.getArchiveReasons = () => axios
    .get('/api/archive-patient-reasons')
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Проверка возможности удаления пациента.
   * @param {number} id
   * @returns {Promise}
   */
  this.checkPatientCanBeRemoved = (id) => axios
    .post(`/api/patient-removed/${id}`)
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 404].includes(error.response.status)) {
          return Fail({ title: error.response.data.title });
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на удаление пациента.
   * @param {number} id
   * @returns {Promise}
   */
  this.sendDeletePatient = (id) => axios
    .delete(`/api/patients/${id}`)
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          let { title } = error.response.data;
          const causeByPatient = error
            .response
            .data
            .causes
            .find((cause) => cause.source === 'patient');
          if (causeByPatient) {
            title += ` ${causeByPatient.details.join('. ')}.`;
          }
          return Fail({ title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          return Fail({ title: error.response.title });
        }
        throw error;
      },
    );
  /**
   * @description Загрузить файлы пациента.
   * @param {number} patientId
   * @param {FormData} form
   * @returns
   */
  this.uploadFilesPatient = (patientId, form) => axios
    .post(`/api/patients/${patientId}/files`, form)
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          return Fail({ title, redirect: true });
        }
        throw error;
      },
    );
  /**
   * @description Загрузить снимки.
   * @param {number} patientId Идентификатор пациента.
   * @param {FormData} form    Форма.
   * @returns {Promise}
   */
  this.uploadSnapshots = async (patientId, form) => {
    try {
      const response = await axios.post(`/api/patients/${patientId}/snapshots`, form);
      const files = response.data.files.map((item) => ({
        id: item.id,
        name: item.name,
        url: item.url,
      }));
      const { message } = response.data;
      return Success({ files, message });
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  this.deleteFilesPatient = (patientId, request) => axios
    .delete(`/api/patients/${patientId}/files`, { data: request })
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const violations = error.response.data || [];
          return Fail(violations);
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          return Fail({ title, redirect: true });
        }
        throw error;
      },
    );
  /**
   * @description Удалить снимок пациента.
   * @param {number} patientId Идентификатор пациента.
   * @param {number} snapshotId Идентификатор снимка.
   * @returns {Promise}
   */
  this.deleteSnapshots = async (patientId, snapshotId) => {
    try {
      const response = await axios.delete(`/api/patients/${patientId}/snapshots/${snapshotId}`);
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  this.downloadPatientFile = (patientId, fileId) => axios
    .get(`/api/patients/${patientId}/files/${fileId}`, { responseType: 'blob' })
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        if (error.isAxiosError) {
          const { status } = error.response;
          if (status === 400) {
            const violations = data || [];
            return Fail(violations);
          }
          if (status === 404 || (status === 500 && json !== '')) {
            const { causes, title } = data;
            const isNotFoundPatient = causes
              .some((cause) => cause.source === 'patient');
            return Fail({ title, redirect: isNotFoundPatient });
          }
        }
        throw error;
      },
    );
  /**
   * @description Скачать снимок пациента.
   * @param {numeric} patientId Идентификатор пациента.
   * @param {*} snapshotId Идентификатор снимка.
   * @returns {Promise}
   */
  this.downloadSnapshot = async (patientId, snapshotId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/snapshots/${snapshotId}`, {
        responseType: 'blob',
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        const violations = data || [];
        return Fail(violations);
      }
      throw error;
    }
  };

  /**
   * @description Отправить запрос на редактирование адреса.
   * @param {EditAddressRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.editAddress = (request) => axios
    .put(`/api/patients/${request.getPatientId()}/address`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить основную информацию пациента.
   * @param {number} patientId
   */
  this.viewBasic = async (patientId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/basic`);
      const makeAddress = (address) => new Address(
        address.address_type_id,
        address.apartment,
        address.building,
        address.city,
        address.district,
        address.postal_code,
        address.street,
      );
      const makeContact = (contacts) => new Contacts(
        contacts.email,
        contacts.is_allowed_mailing,
        contacts.no_call,
        (contacts.phone_list || [])
          .map(
            ({
              type,
              value,
              is_main: isMain,
              has_whats_app: hasWhatsApp,
              note,
            }, index) => ({
              type,
              value: value.replaceAll(/[^0-9+/]/ig, ''),
              isMain,
              hasWhatsApp,
              note: note || '',
              key: index,
            }),
          ),
      );
      const makeDocuments = (documents) => new Documents(
        documents.iin,
        documents.snils,
      );
      const makeIdentification = (identification) => new Identification(
        identification.address,
        identification.number,
        identification.series,
        identification.type,
        identification.issued_date,
        identification.issuer_name,
        identification.department_code,
        identification.inn,
        identification.living_address,
      );
      const makeIndividualData = (individualData) => new IndividualData(
        individualData.has_egisz_data_refusal,
      );
      const makeMedcard = (medcard) => new Medcard(
        medcard.birth_date,
        medcard.birth_date_title,
        medcard.card_created_at,
        medcard.medical_card_number,
        medcard.name,
        medcard.note,
        medcard.patronymic,
        medcard.sex_id,
        medcard.surname,
        medcard.labels,
        medcard.workplace,
      );
      const advertisingChannel = {
        id: response.data.advertising_channel_id,
        name: response.data.advertising_channel_name,
      };
      const actions = response.data.actions || [];
      const addresses = (response.data.addresses || []).map(makeAddress);
      const contacts = makeContact(response.data.contacts);
      const documents = makeDocuments(response.data.documents);
      const identification = makeIdentification(response.data.identification);
      const individualData = makeIndividualData(response.data.individual_data);
      const iemkStatus = makeStatusBySchema(response.data.iemk_status);
      const medcard = makeMedcard(response.data.medical_card);
      const basicFieldsErrors = response.data.basic_fields_errors;
      const medcardFieldsErrors = response.data.medcard_fields_errors;
      const addingDate = response.data.adding_at;
      const egiszDataRefusalSampleDocumentTemplateId = response.data
        .egisz_data_refusal_sample_document_template_id;
      return Success({
        actions,
        addingDate,
        addresses,
        advertisingChannel,
        contacts,
        documents,
        guardians: response.data.guardians,
        identification,
        iemkStatus,
        individualData,
        medcard,
        snilsRequired: response.data.documents.snils_required,
        hasEgisz: response.data.has_egisz,
        basicFieldsErrors,
        medcardFieldsErrors,
        egiszDataRefusalSampleDocumentTemplateId,
      });
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить список снимков пациента.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise}
   */
  this.viewSnapshotList = async (patientId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/snapshots`);
      const list = (response.data.data || [])
        .map((item) => ({ id: item.id, name: item.name, url: item.url }));
      return Success(list);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить статистику пациента.
   * @param {number} id Идентификатор пациента.
   * @returns {Promise<Either>}
   */
  this.viewStatistics = (id) => axios
    .get(`/api/patients/${id}/statistics`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Получить список изображений пациента.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise<Either>}
   */
  this.viewVisitsImageList = (patientId) => axios
    .get(`/api/patients/${patientId}/visits/images`)
    .then(
      async (response) => {
        const list = (response.data.data || [])
          .map((item) => ({
            id: item.id, name: item.name, visitId: item.visit_id, url: item.url,
          }));
        return Either.right(list);
      },
      async (error) => {
        if (error.isAxiosError && [400, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на редактирование анамнеза пациента.
   * @param {EditPatientAnamnesisRequest} request Запрос.
   * @returns {Promise}
   */
  this.editAnamnesis = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/anamnesis`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Отправить запрос на редактирование контактов пациента.
   * @param {EditPatientContactsRequest} request Запрос.
   * @returns {Promise}
   */
  this.editContacts = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/contacts`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Отправить запрос на редактирование полости рта пациента.
   * @param {EditPatientDentalCavitiesRequest} request Запрос.
   */
  this.editDentalCavities = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/dental-cavities`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Отправить запрос на редактирование документов пациента.
   * @param {Object} request Запрос.
   */
  this.editDocuments = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/documents`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Отправить запрос на редактирование удостоверения личности пациента.
   * @param {EditPatientIdentificationRequest} request Запрос.
   */
  this.editIdentification = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/identification-with-address`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить имя удостоверение личности пациента.
   * @param {number} patientId Идентификатор пациента.
   */
  this.getIdentification = async (patientId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/identification-with-address`);
      const formatIssuedDate = (date) => {
        const [y, m, d] = date.split('-');
        return [d, m, y].join('/');
      };
      const makeIdentification = (identification) => new Identification(
        identification.address,
        identification.number,
        identification.series,
        identification.type,
        identification.issued_date ? formatIssuedDate(identification.issued_date) : '',
        identification.issuer_name,
        identification.department_code,
        identification.living_address,
      );
      const identification = makeIdentification(response.data);
      return Success(identification);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Редактировать личные данные пациента
   * @param {EditPatientIndividualDataRequest} request
   */
  this.editIndividualData = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/individual-data`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Отправить запрос на изменение медкарты пациента.
   * @param {EditPatientMedcardRequest} request Запрос.
   */
  this.editMedcard = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/medcard`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить имя пациента.
   * @param {number} patientId Идентификатор пациента.
   */
  this.getPatientName = async (patientId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/name`);
      const patientName = {
        addingDate: response.data.adding_date,
        id: patientId,
        name: response.data.name,
        age: response.data.age,
        birthDate: response.data.birth_date,
        hasAnamnesisErrors: response.data.has_anamnesis_errors,
        hasBasicErrors: response.data.has_basic_errors,
        hasDocumentsErrors: response.data.has_documents_errors,
        archivedDate: response.data.archived_date,
      };
      return Success(patientName);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить список диагнозов для зубной формулы.
   * @returns {Promise<Maybe>}
   */
  this.getDentalFormulaDiagnoses = async () => {
    const diagnosisMapper = (diagnosis) => new Diagnosis(
      diagnosis.id,
      diagnosis.name,
      diagnosis.color,
    );
    try {
      const response = await axios.get('/api/dental-formula-diagnoses');
      const parts = response.data.data.map((part) => [
        part.id,
        part.diagnoses.map(diagnosisMapper),
      ]);
      return Maybe.some(Object.fromEntries(parts));
    } catch {
      return Maybe.none();
    }
  };
  /**
   * @description Отправить запрос на изменение диагнозов части зуба.
   * @param {number} visitId     Идентификатор визита.
   * @param {number} toothNumber Номер зуба.
   * @param {Part}   part        Часть зуба.
   * @returns {Promise<Either>}
   */
  this.editToothPartDiagnoses = async (visitId, toothNumber, part) => {
    const request = {
      diagnoses: part.diagnoses.map((item) => item.id),
    };
    try {
      const response = await axios.put(
        `/api/visits/${visitId}/dental-formula/${toothNumber}/parts/${part.id}/diagnoses`,
        request,
      );
      return Either.right(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Either.left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Оправить запрос на редактирование диагнозов зуба.
   * @param {number} visitId     Идентификатор визита.
   * @param {number} toothNumber Номер зуба.
   * @param {Part[]} parts       Части зуба.
   * @returns {Promise<Either>}
   */
  this.editToothDiagnoses = async (visitId, toothNumber, parts) => {
    const request = {
      parts: parts.map((part) => ({
        part_id: part.id,
        diagnoses: part.diagnoses.map((diagnosis) => diagnosis.id),
      })),
    };
    try {
      const response = await axios.put(
        `/api/visits/${visitId}/dental-formula/${toothNumber}/diagnoses`,
        request,
      );
      return Either.right(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Either.left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Запрос на редактирование представителя пациента
   * @param request
   * @return {Promise}
   */
  this.editGuardian = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/guardians`,
        request.toQuery(),
      );
      const { guardian_id: guardianId, title } = response.data;
      return Success({
        guardianId,
        title,
      });
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Удалить представителя пациента
   * @param {Number} guardianId
   * @return {Promise}
   */
  this.deleteGuardian = async (guardianId) => {
    try {
      const response = await axios.delete(`/api/patient-guardian/${guardianId}`);
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить отображение представителя
   * @param {Number} guardianId
   * @return {Promise<Maybe>}
   */
  this.viewGuardian = async (guardianId) => {
    try {
      const response = await axios.get(`/api/patient-guardians/${guardianId}`);
      return Maybe.Just(response.data);
    } catch (error) {
      return Maybe.Nothing();
    }
  };
  /**
   * @description Изменить персональные данные пациента.
   * @param {EditPatientPersonalDataRequest} request Запрос.
   * @returns {Promise}
   */
  this.editPersonalData = (request) => axios
    .put(`/api/patients/${request.getPatientId()}/personal`, request.toQuery())
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const makeCauses = (causes) => {
          const failurePairs = (causes || [])
            .map((cause) => [cause.source, cause.details]);
          return failurePairs;
        };
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на редактирование зубов.
   * @param {EditTeethRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.editTeeth = (request) => axios
    .put(`/api/visits/${request.getVisitId()}/dental-formula`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Создать договор из шаблона для скачивания.
   * @param {number} patientId  Идентификатор пациента.
   * @param {number} templateId Идентификатор шаблона.
   * @param {string} number     Номер договора.
   * @returns {Promise}
   */
  this.generateContractFromTemplateForDownload = async (patientId, templateId, number) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/contracts/${templateId}`, {
        responseType: 'blob',
        params: { number },
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        const violations = data || [];
        return Fail(violations);
      }
      throw error;
    }
  };
  /**
   * @description Создать договор из шаблона для печати.
   * @param {number} patientId  Идентификатор пациента.
   * @param {number} templateId Идентификатор шаблона.
   * @param {string} number     Номер договора.
   * @returns {Promise}
   */
  this.generateContractFromTemplateForPrint = async (patientId, templateId, number) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/contracts/${templateId}`, {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
        params: { number },
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        const violations = data || [];
        return Fail(violations);
      }
      throw error;
    }
  };
  /**
   * @description Получить сгенерированный документ по образцу для печати.
   * @param {number} sampleId
   * @param {number} patientId
   * @returns {Promise}
   */
  this.generateSampleDocumentForPrint = async (sampleId, patientId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/sample-documents/${sampleId}`, {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        const violations = data || [];
        return Fail(violations);
      }
      throw error;
    }
  };
  /**
   * @description Изменить номер договора.
   * @param {number} patientId  Идентификатор пациента.
   * @param {number} templateId Идентификатор шаблона.
   * @param {string} number     Номер договора.
   * @returns {Promise}
   */
  this.editContractTemplateNumber = (patientId, templateId, number) => axios
    .put(`/api/patients/${patientId}/contracts/${templateId}/number`, { number })
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Изменить дату договора.
   * @param {number} patientId  Идентификатор пациента.
   * @param {number} templateId Идентификатор шаблона.
   * @param {string} isoDate    Входная дата.
   * @returns {Promise}
   */
  this.editContractTemplateDate = (patientId, templateId, isoDate) => axios
    .put(`/api/patients/${patientId}/contracts/${templateId}/date`, { date: isoDate })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Создать документ из шаблона для скачивания.
   * @param {number} patientId Идентификатор пациента.
   * @param {number} templateId Идентификатор шаблона.
   * @param {number} contractId Идентификатор договора.
   * @returns {Promise}
   */
  this.generateDocumentFromTemplateForDownload = async (patientId, templateId, contractId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/documents/${templateId}`, {
        responseType: 'blob',
        params: { contract_id: contractId },
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        const violations = data || [];
        return Fail(violations);
      }
      throw error;
    }
  };
  /**
   * @description Создать вложенные в Пакете документы из шаблонов, для скачивания.
   * @param {number} patientId Идентификатор пациента.
   * @param {number} templateId Идентификатор шаблона Пакета.
   * @param {PatientTemplate} template Объект шаблона пациента.
   * @returns {Promise}
   */
  this.generatePackageDocumentsFromTemplateForDownload = async (
    patientId,
    templateId,
    template,
  ) => {
    try {
      const data = template.toJSON();
      const response = await axios.post(`/api/patients/${patientId}/package-documents/${templateId}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        const violations = data || [];
        return Fail(violations);
      }
      throw error;
    }
  };
  /**
   * @description Создать документ из шаблона для печати.
   * @param {number} patientId  Идентификатор пациента.
   * @param {number} templateId Идентификатор шаблона.
   * @param {string} contractId Идентификатор договора.
   * @returns {Promise}
   */
  this.generateDocumentFromTemplateForPrint = async (patientId, templateId, contractId = '') => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/documents/${templateId}`, {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
        params: { contract_id: contractId },
      });
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        const violations = data || [];
        return Fail(violations);
      }
      throw error;
    }
  };
  /**
   * @description Получить персональные данные пациента.
   * @param {number} patientId
   * @returns {Promise}
   */
  this.getPersonalData = (patientId) => axios
    .get(`/api/patients/${patientId}/personal`)
    .then(
      async (response) => Success(
        new PersonalData(
          response.data.address,
          response.data.medical_card_number,
          response.data.passport_number,
          response.data.passport_serial,
          response.data.passport_who_issued,
          response.data.phone,
        ),
      ),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error;
      },
    );

  /**
   * @description Объединить дубли карт.
   * @param {object} params Параметры объединения.
   * @returns {Promise<Either>}
   */
  this.mergeMedcardDuplications = (params) => axios
    .post('/api/medcard-duplication-merging', params)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError
          && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список черт пациентов.
   * @returns {Promise<any[]>}
   */
  this.viewLabels = async () => {
    const response = await axios.get('/api/patient-labels');
    return (response.data.data || []);
  };

  /**
   * @description Показать фильтры поиска дубликатов медкарт.
   * @param {string} search
   * @returns {Promise<Either>}
   */
  this.viewMedcardDuplicationFilters = (search) => axios
    .get('/api/medcard-duplication-filters', { params: { search } })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать объединение дублей карт.
   * @param {(string[])} patientIds Идентификаторы пациентов.
   * @returns {Promise<Either>}
   */
  this.viewMedcardDuplicationMerging = (patientIds) => axios
    .get('/api/medcard-duplication-merging', { params: { patient_ids: patientIds } })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError
          && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить медкарту для печати.
   * @param {number} patientId Идентификатор пациента.
   * @param {string} format    Формат печати.
   * @param {string} form      Форма печати.
   * @returns {Promise<Either>}
   */
  this.viewMedcardForPrint = (patientId, format, form) => axios
    .post(
      `/api/patients/${patientId}/medcard/print`,
      { format, form },
      {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      },
    ).then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить стандартный номер мед карты пациента
   * @returns {Promise<Either>}
   */
  this.getDefaultPatientMedcardNumber = async () => {
    const requestUrl = '/api/default-medcard-number';
    try {
      const { data } = await axios.get(requestUrl);
      return Success(data);
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response) {
          return Fail(error.response.data);
        }
        throw new Error(`Network error: сервер по адресу ${requestUrl} не ответил на запрос`);
      }
      throw error;
    }
  };
  /**
   * @description Получить профиль пациента для истории записей на прием
   * @param patientId
   * @returns {Promise<Either<unknown, any>>}
   */
  this.getPatientProfileForAppointmentHistory = (patientId) => axios
    .get(`/api/patients/${patientId}/appointment-history-needle`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Создать новый пользовательский шаблон фразы плана лечения
   * @param {string}      field    Поле к которому привязывается фраза
   * @param {string}      phrase   Фраза
   * @param {string|null} groupId Идентификатор группы.
   * @return {Promise<Either>}
   */
  this.createDiaryPhrase = (field, phrase, groupId = null) => axios
    .post('/api/diary-phrase-templates', { field, phrase, group_id: groupId })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать новую группу фраз.
   * @param {string}      field   Поле к которому привязывается группа
   * @param {string}      title   Название
   * @param {string|null} groupId Идентификатор группы.
   * @return {Promise<Either>}
   */
  this.createDiaryGroup = (field, title, groupId = null) => axios
    .post('/api/diary-phrase-groups', { field, title, group_id: groupId })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Редактировать группу фраз плана лечения
   * @param {string} groupId Идентификатор фразы
   * @param {string} title   Новое название.
   * @return {Promise<Either>}
   */
  this.editDiaryGroup = (groupId, title) => axios
    .put(`/api/diary-phrase-groups/${groupId}`, { title })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Редактировать пользовательский шаблон фразы плана лечения
   * @param {string} phraseId Идентификатор фразы
   * @param {string} phrase   Отредактированная фраза
   * @param {string} selectedGroupId Идентификатор выбранной группы.
   * @return {Promise<Either>}
   */
  this.editDiaryPhrase = (phraseId, phrase, selectedGroupId) => axios
    .put(`/api/diary-phrase-templates/${phraseId}`, { phrase, group_id: selectedGroupId })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить из архива.
   * @param {number} patientId
   * @return {Promise}
   */
  this.removeFromArchive = (patientId) => axios
    .delete(`/api/patients/${patientId}/archive`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const data = await error.response.data;
          const { causes, title } = data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Удалить пользовательский шаблон фразы плана лечения
   * @param {string} phraseId Идентификатор фразы
   * @return {Promise<Either>}
   */
  this.removeDiaryPhrase = (phraseId) => axios
    .delete(`/api/diary-phrase-templates/${phraseId}`)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить группу фраз плана лечения
   * @param {string} groupId Идентификатор группы
   * @return {Promise<Either>}
   */
  this.removeDiaryGroup = (groupId) => axios
    .delete(`/api/diary-phrase-groups/${groupId}`)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить библиотеку фраз для протокола приема.
   * @param {string} field Поле протокола.
   * @returns {Promise<Maybe>}
   */
  this.viewDiaryPhraseLibrary = (field) => axios
    .get(`/api/diary-phrase-libraries/${field}`)
    .then(async (response) => Maybe.Some(response.data));

  /**
   * @description Создать новый пользовательский шаблон фразы анамнеза
   * @param {string} field  Поле к которому привязывается фраза
   * @param {string} phrase Фраза
   * @return {Promise<Either>}
   */
  this.createAnamnesisPhrase = (field, phrase) => axios
    .post('/api/anamnesis-phrase-templates', { field, phrase })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Редактировать пользовательский шаблон фразы анамнеза
   * @param {string} phraseId Идентификатор фразы
   * @param {string} phrase   Отредактированная фраза
   * @return {Promise<Either>}
   */
  this.editAnamnesisPhrase = (phraseId, phrase) => axios
    .put(`/api/anamnesis-phrase-templates/${phraseId}`, { phrase })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить пользовательский шаблон фразы анамнеза
   * @param {string} phraseId Идентификатор фразы
   * @return {Promise<Either>}
   */
  this.removeAnamnesisPhrase = (phraseId) => axios
    .delete(`/api/anamnesis-phrase-templates/${phraseId}`)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Поиск дублей карт пациента.
   * @param {object} data Данные поиска.
   * @returns {Promise<Either>}
   */
  this.searchMedcardDuplicationsForPatient = (data) => axios
    .post('/api/medcard-duplication-searches', data)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError
          && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить библиотеку фраз для анамнеза.
   * @param {string} field Поле протокола.
   * @returns {Promise<Maybe>}
   */
  this.viewAnamnesisPhraseLibrary = (field) => axios
    .get(`/api/anamnesis-phrase-libraries/${field}`)
    .then(async (response) => Maybe.Some(response.data));

  /**
   * @description Получить анкету для печати.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise<Either>}
   */
  this.viewQuestionnaireForPrint = (patientId) => axios
    .get(`/api/patients/${patientId}/questionnaire/print`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить страницу представителей.
   * @param {number} patientId
   * @param {number} page
   * @param {string} match
   * @returns {Promise<any>}
   */
  this.getGuardiansPage = (patientId, page, match) => axios
    .get(`/api/guardians-list/${patientId}`, { params: { page, match } })
    .then((response) => {
      const items = response.data.data.map((patient) => {
        const identification = new Identification(
          patient.value.identification.address,
          patient.value.identification.number,
          patient.value.identification.serial_number,
          patient.value.identification.type,
          patient.value.identification.when_issued,
          patient.value.identification.who_issued,
          '',
          patient.value.identification.living_address,
          patient.value.snils,
        );
        const patientWithIdentification = new PatientWithIdentification(
          patient.value.first_name,
          patient.value.last_name,
          patient.value.patronymic,
          patient.value.birth_date,
          patient.value.sex_id,
          patient.value.snils,
          identification,
          patient.value.phone,
          patient.value.phone_type,
        );
        return {
          name: patient.name,
          id: patient.id,
          value: patientWithIdentification,
        };
      });
      return {
        items,
        nextPage: response.data.next_page,
      };
    });

  /**
   * @description Сформировать акт взаиморасчетов с пациентом.
   * @param {number} patientId
   * @param {string} dateFrom
   * @param {string} dateTo
   * @returns {Promise<Either>}
   */
  this.formMutualSettlementsAct = (patientId, dateFrom, dateTo) => axios
    .get(`/api/patients/${patientId}/mutual-settlements/${dateFrom}:${dateTo}`)
    .then(
      (response) => Either.right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Either.left(title);
        }
        throw error;
      },
    );

  /**
   * @description Скачать акт взаиморасчетов с пациентом.
   * @param {number} patientId
   * @param {string} dateFrom
   * @param {string} dateTo
   * @returns {Promise<Either>}
   */
  this.downloadMutualSettlementsAct = (patientId, dateFrom, dateTo) => axios
    .get(
      `/api/patients/${patientId}/mutual-settlements/${dateFrom}:${dateTo}`,
      {
        headers: { accept: 'application/vnd.ms-excel' },
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Either.left(title);
        }
        throw error;
      },
    );
}
